import cx from 'clsx';
import React from 'react';

import useTranslation from '@hooks/useTranslation';

import type { IAIReport } from '@model/aiReport';
import type { ICurrency } from '@model/currency';

import TextFieldComponent from '../TextFieldComponent/TextFieldComponent';
import style from './style.module.scss';

interface IProps {
  actionItemType?: string;
  report: IAIReport;
  showTotalCostSavings?: boolean;
  currency: ICurrency;
  costSavingTemplateCurrency: ICurrency | null;
}

const MetricsFields: React.FC<IProps> = ({
  actionItemType = '',
  report,
  showTotalCostSavings,
  currency,
  costSavingTemplateCurrency,
}) => {
  const { t } = useTranslation();

  if (actionItemType !== 'regular' || report.no_actions_type) {
    return null;
  }

  const metricCurrency = costSavingTemplateCurrency?.symbol || currency.symbol;
  const hasTemplateCurrency = !!costSavingTemplateCurrency?.symbol;

  return (
    <div className={style.metricsFieldsBlock}>
      <div className={style.metricsFieldsTitle}>{t('Metrics Information')}</div>
      <div className={style.metricsFieldsWrapper}>
        <div className={style.metricsFields}>
          <TextFieldComponent label={t('Unplanned downtime avoided')} dataQa={'unplanned-downtime-avoided'}>
            <>
              {report.downtime_avoided_calculated ?? report.downtime_avoided}
              <span className={cx(style.fieldExtra, style.fieldExtraRight)}>
                {t(+report.downtime_type === 0 ? 'Hours' : 'minutes')}
              </span>
            </>
          </TextFieldComponent>
          {!!report.lph_avoided && (
            <TextFieldComponent label={t('Lph avoided')} dataQa={'lph-avoided'}>
              <>
                {report.lph_avoided}
                <span className={cx(style.fieldExtra, style.fieldExtraRight)}>
                  {t(+report.downtime_type === 0 ? 'Hours' : 'minutes')}
                </span>
              </>
            </TextFieldComponent>
          )}
          <TextFieldComponent label={t('Equipment/labor savings')} dataQa={'equipment-lavor-savings'}>
            <>
              <span className={style.fieldExtra}>{metricCurrency}</span>
              {report.equipment_labor_saving_in_currency ?? report.equipment_labor_saving}
            </>
          </TextFieldComponent>
        </div>
        {showTotalCostSavings && (
          <div className={style.metricsTotal}>
            <TextFieldComponent label={t('Multiplication factor')} dataQa={'multiplication-factor'}>
              <>
                <span className={style.fieldExtra}>{metricCurrency}</span>
                {report.multiplication_factor_in_currency ?? report.multiplication_factor}
              </>
            </TextFieldComponent>
            <TextFieldComponent label={t('Cost avoided')} dataQa={'cost-avoided'}>
              <>
                <span className={style.fieldExtra}>{metricCurrency}</span>
                {report.cost_avoided_in_currency ?? report.cost_avoided}
              </>
            </TextFieldComponent>
          </div>
        )}
      </div>
    </div>
  );
};

export default MetricsFields;
